import { create } from 'zustand';
import { persist, PersistStorage } from 'zustand/middleware';

// 检测是否在真正的 Web 浏览器环境中运行（排除 React Native）
const isWeb = typeof window !== 'undefined' && 
              typeof document !== 'undefined' && 
              typeof localStorage !== 'undefined' &&
              typeof navigator !== 'undefined' &&
              navigator.product !== 'ReactNative';

export type SupportedLanguage = 'zh-CN' | 'en-US' | 'zh-TW';

export interface AppState {
  isOnline: boolean;
  isAppReady: boolean;
  theme: 'light' | 'dark' | 'system';
  language: SupportedLanguage;

  setOnline: (isOnline: boolean) => void;
  setAppReady: (isReady: boolean) => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setLanguage: (language: SupportedLanguage) => void;
}

// Storage interface that supports both sync and async operations
interface PlatformStorage {
  getItem: (name: string) => string | null | Promise<string | null>;
  setItem: (name: string, value: string) => void | Promise<void>;
  removeItem: (name: string) => void | Promise<void>;
}

// Storage that will be set by the platform (web/mobile)
let platformStorage: PlatformStorage | null = null;

export const setAppStorage = (customStorage: PlatformStorage) => {
  platformStorage = customStorage;
  // Trigger rehydration after storage is set
  useAppStore.persist.rehydrate();
};

// Custom storage wrapper that delegates to platformStorage and handles async
const createPlatformStorage = (): PersistStorage<Partial<AppState>> => ({
  getItem: async (name) => {
    if (!platformStorage) return null;
    try {
      const str = await Promise.resolve(platformStorage.getItem(name));
      if (!str) return null;
      return JSON.parse(str);
    } catch {
      return null;
    }
  },
  setItem: async (name, value) => {
    if (!platformStorage) return;
    try {
      await Promise.resolve(platformStorage.setItem(name, JSON.stringify(value)));
    } catch (e) {
      console.warn('Failed to save app state:', e);
    }
  },
  removeItem: async (name) => {
    if (!platformStorage) return;
    try {
      await Promise.resolve(platformStorage.removeItem(name));
    } catch (e) {
      console.warn('Failed to remove app state:', e);
    }
  },
});

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      isOnline: true,
      isAppReady: false,
      theme: 'system',
      language: 'zh-CN',

      setOnline: (isOnline) => set({ isOnline }),
      setAppReady: (isAppReady) => set({ isAppReady }),
      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
      },
      setLanguage: (language) => set({ language }),
    }),
    {
      name: 'app-settings',
      storage: createPlatformStorage(),
      skipHydration: true,
      partialize: (state) => ({
        theme: state.theme,
        language: state.language,
      }),
    }
  )
);

// Apply theme to document (web only)
function applyTheme(theme: 'light' | 'dark' | 'system') {
  // Only apply theme on web platform
  if (!isWeb) return;

  const root = window.document.documentElement;
  
  let effectiveTheme = theme;
  if (theme === 'system') {
    effectiveTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  root.classList.remove('light', 'dark');
  root.classList.add(effectiveTheme);
  root.setAttribute('data-theme', effectiveTheme);
}

// Initialize theme on load (web only)
if (isWeb && typeof localStorage !== 'undefined') {
  const stored = localStorage.getItem('app-settings');
  if (stored) {
    try {
      const { state } = JSON.parse(stored);
      if (state?.theme) {
        applyTheme(state.theme);
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  // Listen for system theme changes when using 'system' theme
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const stored = localStorage.getItem('app-settings');
    if (stored) {
      try {
        const { state } = JSON.parse(stored);
        if (state?.theme === 'system') {
          applyTheme('system');
        }
      } catch (err) {
        // Ignore
      }
    }
  });
}
